import React from 'react'
import {
  useFloating,
  flip,
  shift,
  Placement,
  useDismiss,
  useInteractions,
  FloatingFocusManager,
  offset,
  FloatingOverlay,
  autoUpdate,
  autoPlacement,
  useTransitionStatus,
  AutoPlacementOptions
} from '@floating-ui/react'

export interface HeadlessFloatProps {
  placement?: Placement
  anchorEl?: HTMLElement | null
  open: boolean
  children?: React.ReactNode | ((options: { transitionStatus: 'open' | 'close' }) => React.ReactNode)
  flip?: boolean
  shift?: boolean
  autoPlacement?: boolean | Pick<AutoPlacementOptions, 'boundary'>
  offset?: number
  onClose?(open: boolean): unknown
  className?: string
  style?: React.CSSProperties
  strategy?: 'absolute' | 'fixed'
  lockScroll?: boolean
  transitionDuration?: number
}

export function HeadlessFloat({
  placement = 'bottom-start',
  anchorEl,
  open,
  children,
  onClose,
  className,
  style,
  strategy,
  lockScroll,
  transitionDuration,
  ...props
}: HeadlessFloatProps) {
  const { refs, context, floatingStyles } = useFloating({
    placement,
    middleware: [
      props.flip ? flip() : null,
      props.shift ? shift() : null,
      props.offset ? offset(props.offset) : null,
      props.autoPlacement ? autoPlacement(typeof props.autoPlacement === 'object' ? { ...props.autoPlacement } : {}) : null
    ],
    onOpenChange(open) {
      if (!open) {
        onClose && onClose(open)
      }
    },
    open,
    strategy,
    whileElementsMounted: autoUpdate
  })

  React.useEffect(() => {
    refs.setPositionReference(anchorEl || null)
  }, [anchorEl, refs])

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([dismiss])
  const { isMounted, status } = useTransitionStatus(context, { duration: transitionDuration })
  if (isMounted) {
    return (
      <FloatingOverlay lockScroll={lockScroll}>
        <FloatingFocusManager context={context} modal={false}>
          <div className={className} ref={refs.setFloating} style={{ ...style, ...floatingStyles }} {...getFloatingProps()}>
            {typeof children === 'function' ? children({ transitionStatus: status as 'open' | 'close' }) : children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    )
  } else {
    return null
  }
}
